import { Link } from 'react-router-dom';
import { formatDate, formatPeriod, plural } from '../format';

/** What the step reads: structural, so the employee and contractor batches (and their readiness shapes) both fit. */
export interface ReadinessItemLike {
  id: string;
  title: string;
  detail: string;
  actionLabel: string | null;
  actionPath: string | null;
}
export interface ReadinessLike {
  isStale: boolean;
  canContinue: boolean;
  blockers: ReadinessItemLike[];
  warnings: ReadinessItemLike[];
  passed: ReadinessItemLike[];
}
export interface ReadinessBatchLike {
  payDate: string;
  periodStartDate: string | null;
  periodEndDate: string | null;
  readiness: ReadinessLike | null;
}

/** Wording that differs between Pay Employees and Pay Contractors. The defaults are the Pay Employees copy. */
export interface ReadinessCopy {
  backLabel: string;
  continueWithWarnings: string;
  continueClean: string;
}
const EMPLOYEE_COPY: ReadinessCopy = {
  backLabel: 'Back to Pay Employees',
  continueWithWarnings: 'Continue to Review Payslip',
  continueClean: 'Continue to Approve Payslip',
};

const GLYPH = { bad: '⊘', warn: '!', ok: '✓' } as const;

function SectionBar({ tone, children }: { tone: 'bad' | 'warn' | 'ok'; children: string }) {
  return (
    <h4 className={`wc-bar wc-bar--${tone}`}>
      <span className="wc-bar__glyph" aria-hidden="true">{GLYPH[tone]}</span>
      {children}
    </h4>
  );
}

function Items({ items, tone }: { items: ReadinessItemLike[]; tone: 'bad' | 'warn' | 'ok' }) {
  return (
    <ul className="wc-items">
      {items.map((item) => (
        <li key={item.id} className={`wc-item wc-item--${tone}`} data-item-id={item.id}>
          <div>
            <strong>{item.title}</strong>
            <p>{item.detail}</p>
          </div>
          {item.actionPath && item.actionLabel && (
            <Link className="wc-item__link" to={item.actionPath}>{item.actionLabel} ›</Link>
          )}
        </li>
      ))}
    </ul>
  );
}

interface ReadinessStepProps {
  batch: ReadinessBatchLike;
  busy: boolean;
  copy?: ReadinessCopy;
  onBack: () => void;
  onRerun: () => void;
  onContinue: () => void;
}

/** Step 2. Everything shown is the server's readiness result for the batch's current version; nothing is derived here. */
export function ReadinessStep({ batch, busy, onBack, onRerun, onContinue, copy = EMPLOYEE_COPY }: ReadinessStepProps) {
  const r = batch.readiness;
  const stale = r === null || r.isStale;

  return (
    <section aria-labelledby="wc-readiness-title">
      <div className="wc-step-head">
        <h3 id="wc-readiness-title">Readiness Check</h3>
        <div className="wc-pills">
          <div className="wc-pill"><span>Pay Day:</span><strong>{formatDate(batch.payDate)}</strong></div>
          <div className="wc-pill"><span>Pay Period:</span><strong>{batch.periodStartDate && batch.periodEndDate ? formatPeriod(batch.periodStartDate, batch.periodEndDate) : 'Not set'}</strong></div>
        </div>
      </div>

      {stale ? (
        <div className="wc-section wc-section--warn wc-section--stale" role="status" data-testid="readiness-stale">
          <p><strong>{r === null ? 'The readiness check has not been run for this batch.' : 'This batch changed after the last readiness check.'}</strong></p>
          <p>Run the readiness check again before continuing.</p>
        </div>
      ) : (
        <>
          {r.blockers.length > 0 && (
            <div className="wc-section wc-section--bad" data-testid="readiness-blockers">
              <SectionBar tone="bad">{`Blocking Issues (${r.blockers.length})`}</SectionBar>
              <Items items={r.blockers} tone="bad" />
            </div>
          )}
          {r.warnings.length > 0 && (
            <div className="wc-section wc-section--warn" data-testid="readiness-warnings">
              <SectionBar tone="warn">{`Warnings — Review Before Proceeding (${r.warnings.length})`}</SectionBar>
              <Items items={r.warnings} tone="warn" />
            </div>
          )}
          <div className="wc-section wc-section--ok" data-testid="readiness-passed">
            <SectionBar tone="ok">{`Checks Passed (${r.passed.length})`}</SectionBar>
            {r.passed.length > 0 && <Items items={r.passed} tone="ok" />}
          </div>
        </>
      )}

      <div className="wc-footer">
        <p className="wc-footer__msg" role="status" data-testid="readiness-footer">
          <span className={`wc-footer__icon${!stale && r.blockers.length === 0 && r.warnings.length === 0 ? ' wc-footer__icon--ok' : ''}`} aria-hidden="true">{!stale && r.blockers.length === 0 && r.warnings.length === 0 ? '✓' : 'i'}</span>
          <span>{stale
            ? 'Run the readiness check to continue.'
            : r.blockers.length > 0
              ? <><strong>{plural(r.blockers.length, 'blocking issue', 'blocking issues')}</strong> must be resolved before you can proceed.</>
              : r.warnings.length > 0
                ? <><strong>{plural(r.warnings.length, 'warning', 'warnings')}</strong> require your acknowledgement before proceeding.</>
                : <><strong>All Passed!</strong> You can now proceed to reviewing your batch payment.</>}</span>
        </p>
        <div className="wc-footer__actions">
          <button type="button" className="wc-btn wc-btn--outline" onClick={onBack} disabled={busy}>{copy.backLabel}</button>
          {(stale || (r && r.blockers.length > 0)) && (
            <button type="button" className="wc-btn wc-btn--outline" onClick={onRerun} disabled={busy}>Run Readiness Check Again</button>
          )}
          <button type="button" className="wc-btn wc-btn--primary" onClick={onContinue} disabled={busy || stale || !r.canContinue}>
            {r && r.warnings.length > 0 ? copy.continueWithWarnings : copy.continueClean}
          </button>
        </div>
      </div>
    </section>
  );
}
